// PRD §7 + §8: Mentor portal entry. By-invitation only — no public signup
// path exists. This page must read as VIP, not as a generic auth form. The
// audience is operators, ex-founders, ex-VPs who get pitched on dozens of
// "advisor networks" a year; they will judge the brand in 4 seconds.
import { useState } from "react";
import { useLocation } from "wouter";
import { Loader2, AlertCircle, Sparkles, ShieldCheck, Compass, Handshake } from "lucide-react";
import { ForgotPasswordModal } from "@/pages/auth/forgot-password-modal";

const BASE = import.meta.env.BASE_URL;

export default function MentorLogin() {
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password || busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${BASE}api/mentors/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || "Login failed"); return; }
      localStorage.setItem("mentor_token", d.token);
      if (d.needsActivation) { setLocation("/mentor/activate"); return; }
      setLocation("/mentor");
    } catch { setErr("Network error"); }
    finally { setBusy(false); }
  }

  return (
    <div data-mentor-theme="dark" className="min-h-screen bg-[var(--mentor-bg)] text-[var(--mentor-fg)]">
      {/* Subtle radial glow — keeps the page from feeling like a flat black wall */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full bg-[#FF5C00]/[0.08] blur-3xl" />
        <div className="absolute top-1/2 -right-40 w-[480px] h-[480px] rounded-full bg-[#FF5C00]/[0.05] blur-3xl" />
      </div>

      <div className="relative z-10 min-h-screen grid lg:grid-cols-[1.1fr_1fr] gap-0">
        {/* LEFT — hero / positioning */}
        <div className="px-8 lg:px-16 py-10 lg:py-20 flex flex-col">
          <div className="flex items-center gap-2 mb-12">
            <div className="w-7 h-7 rounded bg-[#FF5C00] flex items-center justify-center text-white text-xs font-bold">F</div>
            <div>
              <div className="text-sm font-bold tracking-tight">Favcy Select</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">Mentor Pool</div>
            </div>
          </div>

          <div className="flex-1 max-w-xl">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-[#FF5C00] border border-[#FF5C00]/30 rounded-full px-2.5 py-1 mb-6">
              <Sparkles className="w-3 h-3" /> By invitation only
            </div>

            <h1 className="text-4xl lg:text-5xl font-serif font-bold tracking-tight leading-[1.05] mb-5">
              You were chosen because a founder needs exactly what you've built before.
            </h1>

            <p className="text-base lg:text-lg text-white/75 mb-10 leading-relaxed">
              Favcy Select is a curated mentor pool for India's most-vetted early-stage founders. No cold outreach. No pay-to-play. No "advisory shares for vibes." Just real missions, on your terms, with a team that respects your time.
            </p>

            <div className="space-y-5">
              <Bullet
                icon={<Compass className="w-4 h-4 text-[#FF5C00]" />}
                title="Real missions, not coffee chats"
                body="Each mission has a written problem, a deliverable, and a definition of done — drafted by the founder, sharpened by our PC. You see the brief before you say yes."
              />
              <Bullet
                icon={<Handshake className="w-4 h-4 text-[#FF5C00]" />}
                title="Comp on your terms"
                body="You set rate, equity expectations, and hours/month. The founder agrees in writing before kickoff. Favcy facilitates the introduction; the engagement is yours."
              />
              <Bullet
                icon={<ShieldCheck className="w-4 h-4 text-[#FF5C00]" />}
                title="Recorded, attributable, accountable"
                body="Intro calls run on Meet, recordings live in your shared Drive. Founders rate every engagement. Bad behavior — on either side — gets paused publicly."
              />
            </div>

            <div className="mt-12 pt-6 border-t border-white/10 text-xs text-white/60 leading-relaxed">
              Mentors join only by invitation from PC or by referral from a current mentor. If you believe you should be in this pool and aren't yet, write to <a href="mailto:pc@favcy.in" className="text-[#FF5C00] hover:underline">pc@favcy.in</a> with a one-line pitch and a LinkedIn URL.
            </div>
          </div>
        </div>

        {/* RIGHT — sign-in panel */}
        <div className="px-8 lg:px-12 py-10 lg:py-20 flex items-center justify-center bg-white/[0.02] lg:border-l lg:border-white/10">
          <div className="w-full max-w-sm">
            <div className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-xl p-7 shadow-2xl">
              <h2 className="text-xl font-bold mb-1.5">Sign in</h2>
              <p className="text-sm text-white/60 mb-6">Use the email you received your invitation on.</p>

              <form onSubmit={submit} className="space-y-3">
                <div>
                  <label className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/60 block mb-1.5">Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email"
                    className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#FF5C00]/60 focus:ring-2 focus:ring-[#FF5C00]/15 transition"
                    data-testid="input-mentor-login-email" />
                </div>
                <div>
                  <label className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/60 block mb-1.5">Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password"
                    className="w-full bg-black/30 border border-white/15 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#FF5C00]/60 focus:ring-2 focus:ring-[#FF5C00]/15 transition"
                    data-testid="input-mentor-login-password" />
                </div>
                {err && (
                  <div className="flex items-start gap-1.5 text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-2.5 py-2" data-testid="error-mentor-login">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>{err}</span>
                  </div>
                )}
                <button type="submit" disabled={busy || !email || !password}
                  className="w-full bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-md py-2.5 flex items-center justify-center gap-2 mt-1 transition"
                  data-testid="button-mentor-login-submit">
                  {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {busy ? "Signing in…" : "Sign in"}
                </button>
                <div className="text-center pt-1">
                  <button type="button" onClick={() => setForgotOpen(true)}
                    className="text-xs text-white/60 hover:text-[#FF5C00] transition"
                    data-testid="button-mentor-forgot-password">
                    Forgot your password?
                  </button>
                </div>
              </form>

              <div className="mt-5 pt-5 border-t border-white/10 text-xs text-white/60 leading-relaxed">
                First time here? Open the setup link from your invitation email — it expires in 24 hours. If yours has expired, hit "Forgot your password?" above and we'll mint a fresh one in under a minute.
              </div>
            </div>

            <div className="mt-5 text-center text-[11px] text-white/45">
              Favcy Select · A media-led venture builder
            </div>
          </div>
        </div>
      </div>
      <ForgotPasswordModal audience="mentor" open={forgotOpen} initialEmail={email} onClose={() => setForgotOpen(false)} />
    </div>
  );
}

function Bullet({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 w-7 h-7 rounded-md bg-[#FF5C00]/10 border border-[#FF5C00]/25 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-sm font-semibold mb-0.5">{title}</div>
        <p className="text-sm text-white/65 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
